import { useMemo, useState } from 'react'
import { useGroupComparison } from '../../hooks/useGroupComparison.js'
import { useGroupComparisonBrief } from '../../hooks/useGroupComparisonBrief.js'
import { useCurrentUnit } from '../../hooks/useCurrentUnit.js'
import { AGENT_LABELS } from '../../lib/agents.js'
import KitchenLoader from '../KitchenLoader.jsx'
import AgentRanBadge from '../AgentRanBadge.jsx'
import { Skeleton } from '../Skeleton.jsx'

// GroupComparison — the cross-unit comparison overlay (Ship 7, FINAL).
// Rendered from GroupDashboardScreen when view === 'comparison'.
//
// TWO decoupled data paths (mirrors CombinedReorderPipeline):
//   • useGroupComparison      — the per-unit metric grid (service-role read).
//     The TABLE always renders from these, regardless of the AI brief phase.
//   • useGroupComparisonBrief — the AI comparison paragraph (cached brief +
//     edge fn). Loading / empty / failed here NEVER blocks the table.
//
// House rule (React #310): ALL hooks fire BEFORE any conditional return.
//
// A11Y: leader/laggard cells carry a NON-COLOR signal (▲ / ▼ icon + an
// aria-label) so the status is never conveyed by background color alone.

// Unit color ramp — same approach as CombinedReorderPipeline / KPI tables.
const UNIT_COLORS = [
  { bg: '#EFF6FF', text: '#1D4ED8', dot: '#3B82F6' }, // blue
  { bg: '#FEF3F2', text: '#B42318', dot: '#F04438' }, // red
  { bg: '#ECFDF3', text: '#027A48', dot: '#12B76A' }, // green
  { bg: '#FFFAEB', text: '#B54708', dot: '#F79009' }, // amber
  { bg: '#F4F3FF', text: '#5925DC', dot: '#7A5AF8' }, // violet
  { bg: '#FDF2FA', text: '#C11574', dot: '#EE46BC' }, // pink
  { bg: '#F0F9FF', text: '#026AA2', dot: '#0BA5EC' }, // cyan
]

const WINDOWS = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: '7d' },
  { key: '30d',   label: '30d' },
  { key: '90d',   label: '90d' },
]

const LOADER_MESSAGES = [
  'Lining up each location…',
  'Comparing unit against unit…',
  'Finding the widest gap…',
  'Writing your comparison…',
]

// The 8 metric rows in spec order. `directional` rows get leader/laggard
// tagging + the non-color signal; `kind` drives the cell renderer.
const METRIC_ROWS = [
  { key: 'sales_today',     label: "Today's Sales", kind: 'cell',     directional: true },
  { key: 'sales_window',    label: 'Window Sales',  kind: 'cell',     directional: true },
  { key: 'avg_ticket',      label: 'Avg Ticket',    kind: 'cell',     directional: true },
  { key: 'labor_pct',       label: 'Labor %',       kind: 'cell',     directional: true },
  { key: 'waste_14d',       label: '14d Waste',     kind: 'cell',     directional: true },
  { key: 'items_below_par', label: 'Items Below Par', kind: 'below_par', directional: true },
  { key: 'open_catering',   label: 'Open Catering', kind: 'catering', directional: false },
  { key: 'top_item',        label: 'Top item',      kind: 'top_item', directional: false },
]

export default function GroupComparison({ groupId, onBack }) {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const { switchUnit } = useCurrentUnit()
  const [selectedWindow, setSelectedWindow] = useState('7d')
  const { units, loading, error, refresh } = useGroupComparison(groupId, selectedWindow)
  const { phase, brief, generatedAt, regenerate } = useGroupComparisonBrief(groupId, selectedWindow)

  // Stable color per unit by index in the (name-sorted) units list.
  const unitColorById = useMemo(() => {
    const m = new Map()
    units.forEach((u, idx) => m.set(u.restaurant_id, UNIT_COLORS[idx % UNIT_COLORS.length]))
    return m
  }, [units])

  const briefText = brief?.brief_text || ''

  // ---- Early returns (all hooks above). ----
  if (loading) {
    return (
      <div className="mx-auto max-w-screen-md px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl bg-charcoal-hero p-5 shadow-sm">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-5/6" />
        </section>
        <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <Skeleton className="h-3 w-36" />
          <Skeleton className="mt-4 h-40 w-full" />
        </section>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto max-w-screen-md px-4 py-6">
        <BackButton onBack={onBack} />
        <section className="mt-3 rounded-2xl border border-red-200 bg-red-50/40 p-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-red-900/70">
            {AGENT_LABELS.groupComparison}
          </div>
          <div className="mt-2 text-sm text-red-800">{error}</div>
          <button
            type="button"
            onClick={refresh}
            className="mt-2 rounded-md px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </section>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-md px-4 py-6">
      <BackButton onBack={onBack} />

      {/* ---------- AI comparison brief (charcoal card) ---------- */}
      <section
        className="relative mt-3 rounded-2xl bg-charcoal-hero p-5 shadow-sm"
        aria-label={AGENT_LABELS.groupComparison}
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] font-medium text-charcoal-text-secondary">
            {AGENT_LABELS.groupComparison}
          </div>
          <div className="flex items-baseline gap-2">
            {phase === 'ready' && (
              <AgentRanBadge generatedAt={generatedAt} tone="dark" />
            )}
            {(phase === 'ready' || phase === 'empty' || phase === 'error') && (
              <button
                type="button"
                onClick={() => regenerate({ bypassCache: true })}
                className="text-[11px] text-charcoal-text-secondary hover:text-white"
                aria-label="Refresh comparison brief"
              >
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Generating — KitchenLoader, dark tone (never blue spinner). */}
        {phase === 'generating' && (
          <div className="mt-3">
            <KitchenLoader align="row" size="sm" tone="dark" messages={LOADER_MESSAGES} />
          </div>
        )}

        {phase === 'idle' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Tap to compose your cross-unit comparison.
            </p>
            <button
              type="button"
              onClick={() => regenerate({ bypassCache: true })}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Generate
            </button>
          </div>
        )}

        {phase === 'ready' && briefText && (
          <div className="mt-3 text-[15px] leading-relaxed text-white/90">{briefText}</div>
        )}

        {/* Empty / error — deterministic calm copy. The TABLE below still
            renders (decoupled). */}
        {phase === 'empty' && (
          <div className="mt-3 text-[14px] leading-relaxed text-charcoal-text-tertiary">
            {briefText || "We couldn't compare enough activity across your units yet."}
          </div>
        )}
        {phase === 'error' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Couldn’t pull your group comparison — tap to try again.
            </p>
            <button
              type="button"
              onClick={() => regenerate({ bypassCache: true })}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}
      </section>

      {/* ---------- Window chip row ---------- */}
      <nav aria-label="Comparison window" className="mt-4 overflow-x-auto">
        <div className="inline-flex items-center gap-2 pb-1">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              aria-pressed={selectedWindow === w.key}
              onClick={() => setSelectedWindow(w.key)}
              className={[
                'shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors',
                selectedWindow === w.key
                  ? 'border-black/20 bg-black/5 font-semibold text-black/85'
                  : 'border-black/10 bg-white text-black/60 hover:text-black/80',
              ].join(' ')}
            >
              {w.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ---------- The comparison TABLE (decoupled from the brief) ---------- */}
      <section className="mt-3 rounded-2xl border border-black/10 bg-white p-3 shadow-sm">
        {units.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-black/60">
            No units to compare yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {/* Sticky top-left corner. */}
                  <th
                    scope="col"
                    className="sticky left-0 top-0 z-20 bg-white p-2 text-left text-[11px] font-semibold uppercase tracking-wide text-black/45"
                  >
                    Metric
                  </th>
                  {units.map((u) => {
                    const color = unitColorById.get(u.restaurant_id)
                    return (
                      <th
                        key={u.restaurant_id}
                        scope="col"
                        className="sticky top-0 z-10 bg-white p-1.5 text-center"
                      >
                        <button
                          type="button"
                          onClick={() => switchUnit(u.restaurant_id)}
                          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-semibold transition-opacity hover:opacity-80"
                          style={{ backgroundColor: color?.bg, color: color?.text }}
                          title={`Open ${u.restaurant_name}`}
                        >
                          <span
                            className="inline-block h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: color?.dot }}
                            aria-hidden="true"
                          />
                          <span className="max-w-[8rem] truncate">{u.restaurant_name}</span>
                        </button>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {METRIC_ROWS.map((row) => (
                  <tr key={row.key} className="border-t border-black/5">
                    <th
                      scope="row"
                      className="sticky left-0 z-10 whitespace-nowrap bg-white p-2 text-left text-[12px] font-medium text-black/65"
                    >
                      {row.label}
                    </th>
                    {units.map((u) => (
                      <MetricTd key={u.restaurant_id} row={row} unit={u} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Legend — restate that ▲ leads / ▼ needs attention (non-color a11y). */}
      <div className="mt-2 flex items-center gap-4 px-1 text-[11px] text-black/50">
        <span className="inline-flex items-center gap-1">
          <span aria-hidden="true">▲</span> Leads
        </span>
        <span className="inline-flex items-center gap-1">
          <span aria-hidden="true">▼</span> Needs attention
        </span>
      </div>
    </div>
  )
}

// MetricTd — renders one cell for a (metric row, unit) pair. Directional cells
// carry leader/laggard background AND a ▲/▼ icon + aria-label (non-color
// signal). Neutral display rows (catering, top item) are never tagged.
function MetricTd({ row, unit }) {
  const metrics = unit.metrics || {}

  if (row.kind === 'catering') {
    const c = metrics.open_catering || {}
    return (
      <td className="p-2 text-center align-middle text-[13px] text-black/80">
        <div className="font-semibold">{c.count ?? 0}</div>
        <div className="text-[11px] text-black/50">{c.pipeline_value_formatted || '$0.00'}</div>
      </td>
    )
  }

  if (row.kind === 'top_item') {
    const t = metrics.top_item || {}
    if (t.item_name == null) {
      return (
        <td className="p-2 text-center align-middle text-[13px] text-black/40" aria-label="No top item">
          —
        </td>
      )
    }
    return (
      <td className="p-2 text-center align-middle text-[13px] text-black/80">
        <div className="max-w-[9rem] truncate font-medium">{t.item_name}</div>
        {t.units_sold != null && (
          <div className="text-[11px] text-black/50">{t.units_sold} sold</div>
        )}
      </td>
    )
  }

  // below_par: count + critical sub-line. Still directional (tagged on the
  // items_below_par cell).
  if (row.kind === 'below_par') {
    const belowCell = metrics.items_below_par || {}
    const critCell = metrics.items_critical || {}
    const { bg, signal, label } = statusStyle(belowCell.status)
    const isNull = belowCell.value == null
    return (
      <td className="p-2 text-center align-middle" style={{ backgroundColor: bg }} aria-label={label}>
        {isNull ? (
          <span className="text-[13px] text-black/40">—</span>
        ) : (
          <>
            <div className="inline-flex items-center justify-center gap-1 text-[13px] font-semibold text-black/85">
              {signal && <span aria-hidden="true">{signal}</span>}
              {belowCell.formatted}
            </div>
            {critCell.value != null && Number(critCell.value) > 0 && (
              <div className="text-[11px] font-medium text-red-700/80">
                {critCell.formatted} critical
              </div>
            )}
          </>
        )}
      </td>
    )
  }

  // Generic directional cell (sales_today, sales_window, avg_ticket, labor_pct,
  // waste_14d).
  const cell = metrics[row.key] || {}
  const { bg, signal, label } = statusStyle(cell.status)
  const isNull = cell.value == null
  return (
    <td
      className="p-2 text-center align-middle"
      style={{ backgroundColor: isNull ? 'transparent' : bg }}
      aria-label={isNull ? undefined : label}
    >
      {isNull ? (
        <span className="text-[13px] text-black/40">—</span>
      ) : (
        <span className="inline-flex items-center justify-center gap-1 text-[13px] font-semibold text-black/85">
          {signal && <span aria-hidden="true">{signal}</span>}
          {cell.formatted}
        </span>
      )}
    </td>
  )
}

// statusStyle — leader → light green + ▲ + "leader"; laggard → light red + ▼ +
// "needs attention"; neutral → transparent, no icon/label. The icon + label
// are the NON-COLOR signal so status never relies on background alone.
function statusStyle(status) {
  if (status === 'leader') {
    return { bg: '#ECFDF3', signal: '▲', label: 'leader' }
  }
  if (status === 'laggard') {
    return { bg: '#FEF3F2', signal: '▼', label: 'needs attention' }
  }
  return { bg: 'transparent', signal: null, label: undefined }
}

function BackButton({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 text-sm font-medium text-black/60 hover:text-black/85"
    >
      ← All units
    </button>
  )
}
